import type { Editor } from "@tiptap/core"

interface TiptapDocLike {
  type?: string
  content?: unknown[]
}

/** Replace the editor's document with server-originated content (a
 *  left-panel recompute, or the response of another slot's own autosave)
 *  WITHOUT going through `editor.commands.setContent`. That command only
 *  sets a `preventUpdate` meta (suppresses `onUpdate` — correct, this isn't
 *  a user edit) but never `addToHistory: false`, so the change still lands
 *  as an undoable step. A single Ctrl/Cmd-Z right after — or one merged
 *  into the same undo group as a fast-typed edit right after it, per
 *  prosemirror-history's default `newGroupDelay` — can revert straight back
 *  past it into stale content (old paragraph wording AND old system-fragment
 *  HTML), which then gets reported through `onUpdate` and autosaved as if
 *  the user had typed it.
 *
 *  `addToHistory: false` is prosemirror-history's own documented mechanism
 *  for exactly this shape of problem (a remote/external change a local
 *  undo must never be able to reach) — it does not create an undo step at
 *  all, so undo skips over it entirely rather than landing on it. */
export function applyExternalContent(
  editor: Editor,
  content: TiptapDocLike,
): void {
  const newDoc = editor.schema.nodeFromJSON(content)
  const tr = editor.state.tr
    .setMeta("preventUpdate", true)
    .setMeta("addToHistory", false)
    .replaceWith(0, editor.state.doc.content.size, newDoc.content)
  editor.view.dispatch(tr)
}
